/* components/patterns/chat/MessageList.jsx — лента диалога (S5 + S6).
   Пузыри user/assistant, Markdown, стрим-курсор, умный автоскролл (фикс №29).
   S6: реальные feed-блоки (ToolCallProgress/SourcesBlock/ConfirmActionCard/DraftEntityCard)
   и inline-канвасы (CanvasHost) — вставляются ПОСЛЕ anchor-сообщения (msg.id), не прыгают
   к последнему. Канвасы всегда рендерятся ИНЛАЙН в ленте (правой панели нет).
   Источник: 06 §2.5, 07. ХЕНДОФФ → frontend/components/chat/MessageList.jsx. */

import React from 'react';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { useAuth } from '../../../app/providers/AuthProvider.jsx';
import { tariffTarget } from '../../../lib/aiAccess.js';
import { RR } from '../../../lib/router.jsx';
import { ChatMarkdown as Markdown } from './Markdown.jsx';
import { AttachmentChip } from '../../ui/AttachmentChip.jsx';
import { Button } from '../../ui/Button.jsx';
import { Icon } from '../../ui/Icon.jsx';
import { CanvasHost, canvasSpecFromMeta } from '../../../screens/workspace/feed/CanvasHost.jsx';
import { ConfirmActionCard } from '../../../screens/workspace/feed/ConfirmActionCard.jsx';
import { DraftEntityCard, DraftVersionGroup } from '../../../screens/workspace/feed/DraftEntityCard.jsx';
import { SourcesBlock } from '../../../screens/workspace/feed/SourcesBlock.jsx';
import { ToolCallProgress } from '../../../screens/workspace/feed/ToolCallProgress.jsx';

  const { useRef, useEffect, useState, useCallback } = React;
  const { useNavigate } = RR;

  // фиксированная короткая длительность плавного проматывания ленты вниз при открытии чата
  const OPEN_SCROLL_MS = 280;

  function timeShort(t) {
    const d = (t instanceof Date) ? t : new Date(t);
    if (isNaN(d)) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  function AssistantToolbar({ message, onRegenerate }) {
    const toast = useToast();
    const [vote, setVote] = useState(null);
    const copy = () => {
      try { navigator.clipboard.writeText(message.content || ''); toast.show('Скопировано', 'success'); }
      catch (e) { toast.show('Не удалось скопировать', 'danger'); }
    };
    const regen = () => { if (onRegenerate) onRegenerate(message); };
    const setV = (v) => { setVote(v); toast.show('Спасибо за оценку', 'muted'); };
    return (
      <div className="msg__toolbar">
        <button className="msg__tool" onClick={copy} aria-label="Копировать"><Icon name="copy" size={15} /></button>
        {onRegenerate && (
          <button className="msg__tool" onClick={regen} aria-label="Перегенерировать"><Icon name="refresh" size={15} /></button>
        )}
        <span className="msg__tool-sep" />
        <button className={'msg__tool' + (vote === 'up' ? ' is-on' : '')} onClick={() => setV('up')} aria-pressed={vote === 'up'} aria-label="Хороший ответ"><Icon name="thumbs-up" size={15} /></button>
        <button className={'msg__tool' + (vote === 'down' ? ' is-on' : '')} onClick={() => setV('down')} aria-pressed={vote === 'down'} aria-label="Плохой ответ"><Icon name="thumbs-down" size={15} /></button>
      </div>
    );
  }

  function UserMessage({ message }) {
    return (
      <div className="msg msg--user">
        <div className="msg__col">
          <div className="msg__bubble msg__bubble--user">
            {String(message.content || '').split('\n').map((ln, i) => <p key={i} className="msg__line">{ln}</p>)}
          </div>
          {message.attachments && message.attachments.length > 0 && (
            <div className="msg__atts">
              {message.attachments.map((a) => <AttachmentChip key={a.id} file={a} inFeed />)}
            </div>
          )}
          <div className="msg__meta msg__meta--user num">{timeShort(message.time)}</div>
        </div>
      </div>
    );
  }

  // Квота тарифа исчерпана (429 chat_quota_exceeded): вместо глухой «Ошибка сервера»
  // показываем апселл. Бизнес-пространство → бизнес-тариф (Настройки → Для бизнеса →
  // Тариф; квота у бизнеса общая на ws), личное → персональный тариф (Профиль →
  // Тариф). Текст detail приходит с бэка.
  function QuotaExceededBubble({ detail }) {
    const navigate = useNavigate();
    const { activeWorkspace } = useAuth();
    const target = tariffTarget(activeWorkspace);
    return (
      <div className="msg__bubble msg__bubble--quota" role="alert">
        <div className="msg__quota-head">
          <Icon name="sparkles" size={18} />
          <span className="msg__quota-title">Лимит сообщений тарифа исчерпан</span>
        </div>
        <p className="msg__quota-text">{detail}</p>
        <Button variant="primary" size="sm" iconRight="arrow-up-right"
                onClick={() => navigate(target)}>
          Повысить тариф
        </Button>
      </div>
    );
  }

  // Оплата закончилась (402 subscription_expired). Отдельно от квоты сознательно:
  // квотный текст обещает «окно обновится автоматически», и для отвалившегося это
  // ложь — обновится до демо-лимита, а не до его прежнего тарифа. Раньше такой
  // клиент вообще не получал сигнала: пока демо-лимит не выбран, ассистент просто
  // работал, и «тариф закончился» узнать было неоткуда.
  function SubscriptionEndedBubble({ detail }) {
    const navigate = useNavigate();
    const { activeWorkspace } = useAuth();
    const target = tariffTarget(activeWorkspace);
    return (
      <div className="msg__bubble msg__bubble--quota" role="alert">
        <div className="msg__quota-head">
          <Icon name="credit-card" size={18} />
          <span className="msg__quota-title">Ассистент недоступен</span>
        </div>
        <p className="msg__quota-text">{detail}</p>
        <Button variant="primary" size="sm" iconRight="arrow-up-right"
                onClick={() => navigate(target)}>
          Выбрать тариф
        </Button>
      </div>
    );
  }

  // F40 W4: какие черновики-блоки рендерить ПОД этим сообщением. Версии одной
  // линии (version_group_id) собираются across-messages в галерею, которую
  // показываем у ЯКОРЯ — сообщения с последней версией; superseded-версии в
  // прежних сообщениях не рендерим (они внутри галереи). for_menu-черновики вне
  // версионирования: рендерим standalone и только активную (не superseded).
  function draftBlocksFor(message, versionGroups) {
    const list = Array.isArray(message.metadata && message.metadata.draft_entities)
      ? message.metadata.draft_entities : [];
    const blocks = [];
    const seenGroups = new Set();
    for (const d of list) {
      if (!d || typeof d !== 'object') continue;
      if (d.for_menu) {
        if (!d.superseded) blocks.push({ type: 'card', key: d.proposal_id, draft: d });
        continue;
      }
      const vgid = d.version_group_id || d.proposal_id;
      const group = versionGroups && versionGroups.get(vgid);
      if (!group || group.anchorMsgId !== message.id) continue; // якорь в другом сообщении
      if (seenGroups.has(vgid)) continue;
      seenGroups.add(vgid);
      blocks.push(group.versions.length > 1
        ? { type: 'group', key: vgid, versions: group.versions }
        : { type: 'card', key: vgid, draft: group.versions[0] });
    }
    return blocks;
  }

  // Документы, сгенерированные ботом в легаси (ТТК-карты/отчёты/таблицы), у нас
  // нет структурного канваса — приходят как готовый markdown в metadata. Рендерим
  // read-only «документ»: свёрнутая карточка-вложение, по клику разворачивается.
  const DOC_FMT_LABEL = { pdf: 'PDF', xlsx: 'XLSX', docx: 'DOCX', csv: 'CSV' };
  function MigratedDocCard({ doc }) {
    const [open, setOpen] = useState(false);
    if (!doc || !doc.markdown) return null;
    const isSheet = doc.kind === 'spreadsheet';
    const fmt = DOC_FMT_LABEL[doc.format] || (isSheet ? 'XLSX' : 'PDF');
    return (
      <div className={'migrated-doc' + (open ? ' is-open' : '')}>
        <button className="migrated-doc__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name={isSheet ? 'reports' : 'file-text'} size={16} className="migrated-doc__icon" />
          <span className="migrated-doc__title truncate" title={doc.title}>{doc.title || 'Документ'}</span>
          <span className="migrated-doc__badge">{fmt}</span>
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} className="migrated-doc__chev" />
        </button>
        {open && (
          <div className="migrated-doc__body">
            <Markdown source={doc.markdown} />
          </div>
        )}
      </div>
    );
  }

  function AssistantMessage({ message, onRegenerate, onRevise, onCommittedDraft, onRetryTurn, chatId, versionGroups, suppressPcId }) {
    const md = message.metadata || {};
    const err = md.stream_error;
    const hasContent = !!(message.content && message.content.trim());
    const draftBlocks = draftBlocksFor(message, versionGroups);
    return (
      <div className="msg msg--assistant">
        <div className="msg__avatar" aria-hidden="true"><img src="/logo-mark.svg" alt="" width={22} height={22} /></div>
        <div className="msg__col">
          <div className="msg__meta"><span className="msg__author">Ресториум</span> <span className="num">{timeShort(message.time)}</span></div>

          {err ? (
            md.stream_error_code === 'chat_quota_exceeded' ? (
              <QuotaExceededBubble detail={err} />
            ) : md.stream_error_code === 'subscription_expired' ? (
              <SubscriptionEndedBubble detail={err} />
            ) : (
              <>
                <div className="msg__bubble msg__bubble--error" role="alert">
                  <Icon name="x-circle" size={16} /> <span>{err}</span>
                </div>
                {onRetryTurn && (
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" variant="secondary" onClick={() => onRetryTurn(message)}>
                      Повторить
                    </Button>
                  </div>
                )}
              </>
            )
          ) : (
            <>
              {(hasContent || message.streaming) && (
                <div className="msg__bubble">
                  {hasContent && <Markdown source={message.content} />}
                  {message.streaming && <span className="msg__cursor" aria-hidden="true" />}
                </div>
              )}
              {Array.isArray(md.migrated_documents) && md.migrated_documents.map((d, i) => (
                <MigratedDocCard key={(d && d.legacy_id) || i} doc={d} />
              ))}
              <ToolCallProgress calls={message.toolCalls} streaming={message.streaming} />
              {draftBlocks.map((b) => (b.type === 'group'
                ? <DraftVersionGroup key={b.key} versions={b.versions} chatId={chatId} onRevise={onRevise} onCommitted={onCommittedDraft} />
                : <DraftEntityCard key={b.key} draft={b.draft} messageId={b.draft._msgId || message.id} chatId={chatId} onRevise={onRevise} onCommitted={onCommittedDraft} />
              ))}
              {/* Компакт-карточку правки прячем, пока ОТКРЫТ канвас изменений этой же
                  правки (suppressPcId) — одна поверхность одобрения за раз; закрыли
                  канвас → карточка возвращается и подхватывает статус из pc.status. */}
              {Array.isArray(md.pending_changes) && md.pending_changes
                .filter((pc) => pc.id !== suppressPcId)
                .map((pc) => <ConfirmActionCard key={pc.id} pc={pc} />)}
              <SourcesBlock sources={md.sources} />
              {!message.streaming && hasContent && <AssistantToolbar message={message} onRegenerate={onRegenerate} />}
            </>
          )}
        </div>
      </div>
    );
  }

  const CANVAS_LABEL = { ttk: 'Карта ТТК', menu: 'Проект меню', abc: 'ABC-анализ', foodcost: 'Разбор food cost', inventory: 'Анализ инвентаризации' };
  function specTitle(spec) {
    if (!spec) return 'Канвас';
    return (spec.partial && spec.partial.title) || CANVAS_LABEL[spec.type] || 'Канвас';
  }

  /* инлайн-канвас в ленте — единственная форма (правой панели нет). */
  function FeedCanvas({ spec, onClose }) {
    return (
      <div className="feed-canvas">
        <CanvasHost spec={spec} onClose={onClose} />
      </div>
    );
  }

  function MessageList({ messages = [], onRegenerate, onRevise, onCommittedDraft, onRetryTurn, streamingId, closedCanvases, onCloseCanvas, chatId }) {
    const scrollRef = useRef(null);
    const pinnedRef = useRef(true);
    const prevLenRef = useRef(messages.length);
    const openScrollRef = useRef(true);   // при маунте/смене чата ленту нужно промотать вниз
    const rafRef = useRef(0);             // хэндл анимации проматывания
    const closed = closedCanvases || {};
    // Перегенерацию предлагаем только у ПОСЛЕДНЕГО сообщения ленты (бэк регенерит
    // лишь хвост → 409 на не-последнем; для structured-чатов onRegenerate=null).
    const lastId = messages.length ? messages[messages.length - 1].id : null;

    const isNearBottom = () => {
      const el = scrollRef.current; if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    const cancelAnim = () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; } };
    const scrollToBottom = useCallback(() => {
      cancelAnim();
      const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight;
    }, []);
    // Плавное проматывание вниз за фиксированное короткое время (открытие чата).
    // Цель пересчитываем каждый кадр — высота контента (markdown/канвасы/картинки)
    // может дорастать уже после старта, и анимация всё равно доедет до самого низа.
    const animateToBottom = useCallback((duration) => {
      const el = scrollRef.current; if (!el) return;
      cancelAnim();
      const start = el.scrollTop;
      const ease = (t) => 1 - Math.pow(1 - t, 3);   // easeOutCubic
      let t0 = null;
      const step = (ts) => {
        if (t0 === null) t0 = ts;
        const p = duration > 0 ? Math.min(1, (ts - t0) / duration) : 1;
        const target = el.scrollHeight - el.clientHeight;
        el.scrollTop = start + (target - start) * ease(p);
        if (p < 1) { rafRef.current = requestAnimationFrame(step); }
        else { el.scrollTop = el.scrollHeight; rafRef.current = 0; }
      };
      rafRef.current = requestAnimationFrame(step);
    }, []);
    const onScroll = () => { pinnedRef.current = isNearBottom(); };

    // открытие/смена чата → промотать ленту вниз (флаг отработает, когда подъедут сообщения)
    useEffect(() => {
      openScrollRef.current = true;
      pinnedRef.current = true;
      prevLenRef.current = 0;        // смена чата ≠ «рост» ленты
      return cancelAnim;             // оборвать незавершённую анимацию при смене чата/размонтировании
    }, [chatId]);

    // автоскролл: при открытии чата — плавно за фикс. время; при росте ленты у низа —
    // мгновенно (не дёргаем при правке черновика — там длина ленты не меняется).
    useEffect(() => {
      const grew = messages.length > prevLenRef.current;
      const lastIsUser = messages.length && messages[messages.length - 1].role === 'user';
      prevLenRef.current = messages.length;
      if (openScrollRef.current && messages.length) {
        openScrollRef.current = false;
        animateToBottom(OPEN_SCROLL_MS);
        return;
      }
      if (lastIsUser || (grew && pinnedRef.current)) scrollToBottom();
    }, [messages.length, chatId, scrollToBottom, animateToBottom]);

    // F40 W4: индекс линий черновиков (version_group_id → версии across-messages,
    // отсортированы по version_n; anchor = сообщение последней версии). Галерея
    // версий рендерится у якоря; for_menu-черновики из индекса исключены.
    const versionGroups = React.useMemo(() => {
      const acc = new Map();
      for (const m of messages) {
        if (m.role !== 'assistant') continue;
        const list = (m.metadata && m.metadata.draft_entities) || [];
        for (const d of list) {
          if (!d || typeof d !== 'object' || d.for_menu) continue;
          const vgid = d.version_group_id || d.proposal_id;
          if (!acc.has(vgid)) acc.set(vgid, []);
          acc.get(vgid).push({ ...d, _msgId: m.id });
        }
      }
      const out = new Map();
      for (const [vgid, versions] of acc) {
        // version_n с legacy-фолбэком (история не проходит через adaptDraftEntity).
        for (const v of versions) v.version_n = v.version_n || (v.rev || 0) + 1;
        versions.sort((a, b) => a.version_n - b.version_n);
        // superseded ВЫВОДИМ из max(version_n) в линии (бэк его не персистит —
        // устойчиво к обрыву хода: последняя версия всегда активна), committed —
        // не superseded.
        const maxN = versions[versions.length - 1].version_n;
        for (const v of versions) v.superseded = v.status !== 'committed' && v.version_n < maxN;
        out.set(vgid, { versions, anchorMsgId: versions[versions.length - 1]._msgId });
      }
      return out;
    }, [messages]);

    return (
      <div className="msg-list" ref={scrollRef} onScroll={onScroll}>
        <div className="msg-list__inner">
          {messages.map((m) => {
            const spec = m.role === 'assistant' ? (canvasSpecFromMeta && canvasSpecFromMeta(m.metadata)) : null;
            const canvasClosed = !!closed[m.id];
            // Канвас изменений открыт → его pending-правку в компакт-карточке не дублируем.
            const suppressPcId = (spec && spec.type === 'change' && !canvasClosed && spec.pc)
              ? spec.pc.id : null;
            // регенерацию не предлагаем для активного стрима и не у последнего сообщения
            const regenFor = (onRegenerate && m.id === lastId && m.id !== streamingId) ? onRegenerate : null;
            // F41: «Повторить» — только у ПОСЛЕДНЕГО сообщения и не во время
            // стрима (бэк ретраит только последний ход; иначе ответ ушёл бы в
            // чужой пузырь / зациклил 409).
            const retryFor = (onRetryTurn && m.id === lastId && m.id !== streamingId) ? onRetryTurn : null;
            return (
              <div className="feed-item" key={m.id}>
                {m.role === 'user'
                  ? <UserMessage message={m} />
                  : <AssistantMessage message={m} onRegenerate={regenFor} onRevise={onRevise} onCommittedDraft={onCommittedDraft} onRetryTurn={retryFor} chatId={chatId} versionGroups={versionGroups} suppressPcId={suppressPcId} />}
                {spec && !canvasClosed && (
                  <FeedCanvas
                    spec={spec}
                    onClose={onCloseCanvas ? () => onCloseCanvas(m.id) : null}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

export { MessageList };
